import { compileMerryMarkdown } from "./markdownParser";
import {
  MERRY_MARKDOWN_MAX_SOURCE_BYTES,
  type MerryMarkdownDocument,
} from "./markdownSchema";

const workerThresholdBytes = 32 * 1024;
const encoder = new TextEncoder();
let worker: Worker | null = null;
let requestID = 0;
const pending = new Map<
  number,
  {
    reject: (reason: Error) => void;
    resolve: (document: MerryMarkdownDocument) => void;
  }
>();

interface MarkdownWorkerResponse {
  document?: MerryMarkdownDocument;
  error?: string;
  id: number;
}

function rejectPending(reason: Error) {
  for (const request of pending.values()) request.reject(reason);
  pending.clear();
}

function getWorker() {
  if (worker) return worker;
  worker = new Worker(new URL("./markdownWorker.ts", import.meta.url), {
    name: "merry-markdown",
    type: "module",
  });
  worker.addEventListener("message", (event: MessageEvent<MarkdownWorkerResponse>) => {
    const request = pending.get(event.data.id);
    if (!request) return;
    pending.delete(event.data.id);
    if (event.data.document) {
      request.resolve(event.data.document);
      return;
    }
    request.reject(new Error(event.data.error ?? "markdown_worker_failed"));
  });
  worker.addEventListener("error", () => {
    rejectPending(new Error("markdown_worker_failed"));
    worker?.terminate();
    worker = null;
  });
  return worker;
}

export function shouldUseMarkdownWorker(source: string) {
  const size = encoder.encode(source).byteLength;
  return size > workerThresholdBytes && size <= MERRY_MARKDOWN_MAX_SOURCE_BYTES;
}

export async function compileMerryMarkdownInWorker(source: string) {
  if (typeof Worker === "undefined") {
    return compileMerryMarkdown(source, { cache: false });
  }
  const id = ++requestID;
  const activeWorker = getWorker();
  return await new Promise<MerryMarkdownDocument>((resolve, reject) => {
    pending.set(id, { reject, resolve });
    try {
      activeWorker.postMessage({ id, source });
    } catch (error) {
      pending.delete(id);
      reject(error instanceof Error ? error : new Error("markdown_worker_failed"));
    }
  });
}
